<template>
  <div class="roundactions" ref="mainElement">
    <!-- icons -->
    <div class="roundactions__actions" @click="toggleActionsPopover">
      <!-- combined -->
      <template v-if="small">
        <span
          v-if="
            incidents.length + actions.length + scheduledEffects.length > 0
          "
          class="roundactions__action"
        >
          <span class="roundactions__action__number"
            >{{ actions.length + incidents.length + scheduledEffects.length }}
          </span>
        </span>
      </template>

      <!-- seperate icons -->
      <template v-if="!small">
        <span v-if="incidents.length > 0" class="roundactions__action">
          <span class="roundactions__action__number"
            >{{ incidents.length }}
          </span>
          <store-item-icon type="incident" class="roundactions__action__icon" />
        </span>
        <span v-if="actions.length > 0" class="roundactions__action">
          <span class="roundactions__action__number"
            >{{ actions.length }}
          </span>
          <store-item-icon type="action" class="roundactions__action__icon" />
        </span>
        <span v-if="scheduledEffects.length > 0" class="roundactions__action">
          <span class="roundactions__action__number"
            >{{ scheduledEffects.length }}
          </span>
          <store-item-icon type="impulse" class="roundactions__action__icon" />
        </span>
      </template>

      <button
        v-if="
          endReason &&
          endReason.endId != null &&
          endReason.roundIndex == roundIndex
        "
        type="button"
        class="roundactions__endbutton"
        @click.prevent="onEndClick()"
      >
        <lucide-icon name="x" />
      </button>
    </div>

    <!-- popover -->
    <round-actions-popover
      :visible="actionsOpen"
      :x="popoverX"
      :y="popoverY"
      :simulation-id="simulationId"
      :round-index="roundIndex"
      :max-height="maxHeight"
      :impulse-source="impulseSource"
      :hide-invisibles="hideInvisibles"
      @close="actionsOpen = false"
      @update:impulse-source="setImpulseSource($event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useModelStore } from "@editor/items/model/model.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";

import StoreItemIcon from "../StoreItemIcon.vue";
import RoundActionsPopover from "./RoundActionsPopover.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

const mainElement = useTemplateRef("mainElement");

const endReason = computed(() => {
  return useSimulationStore().items[props.simulationId]?.endReason;
});

interface Props {
  simulationId: number;
  roundIndex: number;
  width?: number;
  maxHeight?: number;
  hideInvisibles?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  width: 100,
  maxHeight: undefined,
  hideInvisibles: false
});

const popoverX = ref(0);
const popoverY = ref(0);

const simulation = computed(() => {
  return useSimulationStore().items[props.simulationId];
});

const round = computed(() => {
  if (props.simulationId != null && props.roundIndex != null)
    return simulation.value.rounds[props.roundIndex];
  return null;
});

const roundActions = computed(() => {
  return useSimulationStore().getActionPlan(
    props.simulationId,
    props.roundIndex
  );
});

const actions = computed(() => {
  if (roundActions.value == null) return [];

  return useActionStore().getArrayByIds(roundActions.value.plannedActionIds);
});

const incidents = computed(() => {
  if (round.value === null) return [];

  const activatedIncidents = round.value?.activatedIncidents ?? [];
  return useIncidentStore().getArrayByIds(
    activatedIncidents.map((r) => r.incidentId)
  ).filter((incident) => incident != null && (props.hideInvisibles ? incident.visibility != "hidden" : true));
});

const scheduledEffects = computed(() => {
  if (roundActions.value == null) return [];

  const scheduledEffects = roundActions.value.scheduledEffects;

  const modelStore = useModelStore();
  return scheduledEffects.map((impulse) => ({
    ...impulse,
    title: modelStore.getItem(impulse.affectedType, impulse.affectedId)?.title
  }));
});

const impulseSource = computed(() => {
  return useSimulationViewStore().selectedImpulseSource;
});

const small = computed(() => {
  const hasIncidents = incidents.value.length > 0 ? 1 : 0;
  const hasActions = actions.value.length > 0 ? 1 : 0;
  const hasImpulses = scheduledEffects.value.length > 0 ? 1 : 0;

  if (hasIncidents && hasActions && hasImpulses) {
    return props.width < 55;
  }
  if (hasIncidents + hasActions + hasImpulses > 1) {
    return props.width < 40;
  }
  return props.width < 25;
});

const actionsOpen = computed({
  get() {
    return (
      useSimulationViewStore().selectedImpulseSourceRoundIndex ===
      props.roundIndex
    );
  },
  set(open: boolean) {
    if (open) {
      useSimulationViewStore().set({
        selectedImpulseSourceRoundIndex: props.roundIndex
      });
    } else {
      useSimulationViewStore().set({
        selectedImpulseSource: null,
        selectedImpulseSourceRoundIndex: null
      });
    }
  }
});

function setImpulseSource(
  item:
    | { type: "action" | "incident"; id: number }
    | { type: "scheduledEffect"; id: string }
    | null
) {
  useSimulationViewStore().set({
    selectedImpulseSource: item,
    selectedImpulseSourceRoundIndex: props.roundIndex
  });
}

function toggleActionsPopover(mouseEvent: MouseEvent) {
  if (mouseEvent.defaultPrevented) return;
  if (actionsOpen.value) {
    actionsOpen.value = false;
  } else {
    setImpulseSource(null);
    const mainRect = mainElement.value?.getBoundingClientRect();
    const width = mainRect?.width || 0;
    popoverX.value = (mainRect?.left || 0) + width / 2;
    popoverY.value = (mainRect?.top || 0) - 25;
  }
}

function onEndClick() {
  if (!endReason.value) return;
  if (endReason.value.endId == null) return;
  useSimulationViewStore().showScenarioEndPopup({
    scenarioEndId: endReason.value?.endId
  });
}
</script>

<style lang="scss">
.roundactions {
  min-height: 1.5em;
  &__actions {
    text-align: center;
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    overflow: hidden;
  }

  &__action {
    white-space: nowrap;
    margin: 0 0.2em;
    color: var(--color-text-medium);
    &__number {
      display: inline-block;
      vertical-align: middle;
    }
    &__icon {
      display: inline-block;
      vertical-align: middle;
      margin: 0 0.1em;
      font-size: 9px;
    }
  }

  &__endbutton {
    padding: 1px;
    line-height: 1;
    height: 15px;
    width: 15px;
    border: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: var(--color-text-medium);
    color: var(--color-background-deep);
    &:hover {
      background-color: var(--color-text);
    }
  }
}
</style>
